import { z } from 'zod';
import { toast } from 'sonner';
import { Role } from '@/types';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { SYSTEM_ROLES } from '@/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateRole, useUpdateRole } from '@/hooks';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

// Zod schema for role validation
const roleSchema = z.object({
  name: z.string()
    .min(1, 'Role name is required')
    .min(2, 'Role name must be at least 2 characters')
    .max(50, 'Role name must be less than 50 characters')
    .regex(/^[a-zA-Z0-9\s_-]+$/, 'Role name can only contain letters, numbers, spaces, hyphens, and underscores'),
  description: z.string()
    .min(1, 'Role description is required')
    .min(5, 'Role description must be at least 5 characters')
    .max(200, 'Role description must be less than 200 characters'),
  status: z.enum(['ENABLED', 'DISABLED'], {
    required_error: 'Status is required',
  }),
});

type RoleFormData = z.infer<typeof roleSchema>;

interface RoleFormModalProps {
  mode: 'create' | 'edit';
  role?: Role;
  isOpen: boolean;
  onClose: () => void;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({ mode, role, isOpen, onClose }) => {
  const { t } = useTranslation();
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();

  const isEditMode = mode === 'edit';
  const isSystemRole = isEditMode && role ? SYSTEM_ROLES.includes(role.name as typeof SYSTEM_ROLES[number]) : false;

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: '',
      description: '',
      status: 'ENABLED',
    },
  });

  // Reset form when role changes or modal opens/closes
  useEffect(() => {
    if (isEditMode && role) {
      form.reset({
        name: role.name || '',
        description: role.description || '',
        status: role.status || 'ENABLED',
      });
    } else if (!isEditMode) {
      form.reset({
        name: '',
        description: '',
        status: 'ENABLED',
      });
    }
  }, [role, isEditMode, form, isOpen]);

  const onSubmit = async (data: RoleFormData) => {
    try {
      if (isEditMode && role) {
        // Prevent submission for system roles
        if (isSystemRole) {
          toast.warning(t('processManagement.systemRolesCannotBeModified'));
          return;
        }

        await updateRoleMutation.mutateAsync({
          id: role.id,
          formData: data,
        });
        toast.success(t('processManagement.roleUpdatedSuccessfully'));
      } else {
        await createRoleMutation.mutateAsync(data);
        toast.success(t('processManagement.roleCreatedSuccessfully'));
      }
      
      form.reset();
      onClose();
    } catch (error) {
      const errorMessage = isEditMode 
        ? t('processManagement.updatingRoleFailed')
        : t('processManagement.roleCreationFailed');
      toast.error(errorMessage);
    }
  };

  const isLoading = createRoleMutation.isPending || updateRoleMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('processManagement.editRole') : t('processManagement.addNewUserRole')}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? t('processManagement.editRoleDescription') 
              : t('settings.roles.addNewUserRoleDescription')
            }
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('processManagement.roleName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('settings.roles.enterRoleName')}
                      className="w-full"
                      disabled={isSystemRole}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('processManagement.roleDescription')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('settings.roles.enterRoleDescription')}
                      className="w-full"
                      disabled={isSystemRole}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('processManagement.status')}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isSystemRole}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('processManagement.selectStatus')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ENABLED">
                        {t('processManagement.enabled')}
                      </SelectItem>
                      <SelectItem value="DISABLED">
                        {t('processManagement.disabled')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isSystemRole && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  {t('processManagement.noteThisIsASystemRoleAndCannotBeModified')}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSystemRole || isLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isLoading 
                  ? (isEditMode ? t('common.saving') : t('processManagement.creating'))
                  : (isEditMode ? t('common.save') : t('processManagement.add'))
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RoleFormModal;
